import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../Skeleton';
import styles from './Textarea.module.scss';

const Textarea = ({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  name,
  maxLength,
  charLimit,
  required = false,
  disabled = false,
  error = false,
  errorMessage = '',
  helperText = '',
  rows = 4,
  resize = 'vertical',
  showDragHandle = false,
  isLoading = false,
  className = '',
  textareaClassName = '',
  ...rest
}) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value, e.target.name, e);
    }
  };

  const resizeClass = {
    none: styles.noResize,
    vertical: '',
    horizontal: styles.resizeHorizontal,
    both: styles.resizeBoth,
  }[resize];

  const textareaClasses = [
    styles.textarea,
    error && styles.error,
    resizeClass,
    textareaClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.textareaWrapper} ${className}`}>
      {(label || charLimit) && (
        <div className={styles.labelRow}>
          {label && (
            <label className={styles.label} htmlFor={name}>
              {label}
              {required && <span className={styles.required}>*</span>}
            </label>
          )}
          {charLimit && (
            <span className={styles.charLimit}>
              {/* {typeof charLimit === 'string' ? charLimit : `Max ${charLimit} characters`} */}
              {`Max ${maxLength} characters`}
            </span>
          )}
        </div>
      )}
      <div className={styles.textareaContainer}>
        {isLoading ? (
          <Skeleton height={`${rows * 24 + 16}px`} borderRadius="8px" />
        ) : (
          <>
            <textarea
              id={name}
              name={name}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              placeholder={placeholder}
              maxLength={maxLength}
              rows={rows}
              disabled={disabled}
              className={textareaClasses}
              aria-invalid={error}
              aria-describedby={
                error && errorMessage ? `${name}-error` : helperText ? `${name}-helper` : undefined
              }
              {...rest}
            />
            {showDragHandle && resize !== 'none' && <div className={styles.dragHandle} />}
          </>
        )}
      </div>
      {error && errorMessage && (
        <div id={`${name}-error`} className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}
      {!error && helperText && (
        <div id={`${name}-helper`} className={styles.helperText}>
          {helperText}
        </div>
      )}
    </div>
  );
};

Textarea.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  maxLength: PropTypes.number,
  charLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  rows: PropTypes.number,
  resize: PropTypes.oneOf(['none', 'vertical', 'horizontal', 'both']),
  showDragHandle: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  textareaClassName: PropTypes.string,
};

export default Textarea;
